import { createAction, createAsyncAction } from "typesafe-actions";
import creativesConstants from "store/features/materialSpecs/specs/specs.constants";
import {
    CreativesUploadViewModel,
    CreativesViewModel,
    MaterialDetailsViewModel,
    MaterialSpecificationViewModel,
} from "api/factories/creatives.factory";
import {
    ThirdPartyViewModel,
    ThirdPartyUpdateValues,
    NewMaterialThirdPartyValues,
} from "validation/components/ThirdPartyTag/thirdPartyTagRules";
import {
    AdMaterialReq,
    MaterialSpecActionType,
    MatSpecsNewThirdPartyMaterial,
    Permission,
    PreflightReport,
    PrintMaterialAdServerProperties,
    PrintMaterialDescription,
    PrintPreflightReport,
    Status,
} from "api/creatives";
import { TabTypes } from "pages/MaterialSpecs/components";

const {
    FETCH_BOOKINGS_REQUEST,
    FETCH_BOOKINGS_SUCCESS,
    FETCH_BOOKINGS_FAILED,
    UPLOAD_MATERIALS_REQUEST,
    UPLOAD_MATERIALS_SUCCESS,
    UPLOAD_MATERIALS_FAILED,
    FETCH_THIRD_PARTY_SCRIPT_REQUEST,
    FETCH_THIRD_PARTY_SCRIPT_SUCCESS,
    FETCH_THIRD_PARTY_SCRIPT_FAILED,
    SUBMIT_THIRD_PARTY_TAG_REQUEST,
    SUBMIT_THIRD_PARTY_TAG_SUCCESS,
    SUBMIT_THIRD_PARTY_TAG_FAILED,
    SUBMIT_NEW_CREATIVE_THIRD_PARTY_TAG_REQUEST,
    SUBMIT_NEW_CREATIVE_THIRD_PARTY_TAG_SUCCESS,
    SUBMIT_NEW_CREATIVE_THIRD_PARTY_TAG_FAILED,
    FETCH_VALIDATION_REPORT_REQUEST,
    FETCH_VALIDATION_REPORT_SUCCESS,
    FETCH_VALIDATION_REPORT_FAILED,
    FETCH_PRINT_VALIDATION_REPORT_REQUEST,
    FETCH_PRINT_VALIDATION_REPORT_SUCCESS,
    FETCH_PRINT_VALIDATION_REPORT_FAILED,
    REMOVE_MATERIAL_REQUEST,
    REMOVE_MATERIAL_SUCCESS,
    REMOVE_MATERIAL_FAILED,
    UPDATE_MATERIAL_SPEC_STATUS_REQUEST,
    UPDATE_MATERIAL_SPEC_STATUS_SUCCESS,
    UPDATE_MATERIAL_SPEC_STATUS_FAILED,
    SET_MATERIAL_WORKING_STATUS,
    SET_NEW_MATERIAL_VERSION,
    SUBMIT_PRINT_MATERIAL_DESCRIPTION_REQUEST,
    SUBMIT_PRINT_MATERIAL_DESCRIPTION_SUCCESS,
    SUBMIT_PRINT_MATERIAL_DESCRIPTION_FAILED,
    SUBMIT_PRINT_MATERIAL_AD_SERVER_PROPERTIES_REQUEST,
    SUBMIT_PRINT_MATERIAL_AD_SERVER_PROPERTIES_SUCCESS,
    SUBMIT_PRINT_MATERIAL_AD_SERVER_PROPERTIES_FAILED,
    UPDATE_UPLOADED_MATERIAL,
    UPDATE_UPLOADED_MATERIAL_STATUS,
    FETCH_MATERIAL_SPEC_REQUEST,
    FETCH_MATERIAL_SPEC_SUCCESS,
    FETCH_MATERIAL_SPEC_FAILED,
    SET_SELECTED_PAGE,
} = creativesConstants;

export const fetchBookings = createAsyncAction(FETCH_BOOKINGS_REQUEST, FETCH_BOOKINGS_SUCCESS, FETCH_BOOKINGS_FAILED)<
    void,
    CreativesViewModel,
    ErrorHandler
>();

export const uploadMaterials = createAsyncAction(
    UPLOAD_MATERIALS_REQUEST,
    UPLOAD_MATERIALS_SUCCESS,
    UPLOAD_MATERIALS_FAILED
)<
    {
        files: File[];
        materialSpecId: number;
        materialId?: number;
        updateMaterialIds(materialSpecId: number, materialId: number): void;
        selectedTab: TabTypes;
    },
    CreativesUploadViewModel,
    ErrorHandler
>();

export const fetchThirdPartyTag = createAsyncAction(
    FETCH_THIRD_PARTY_SCRIPT_REQUEST,
    FETCH_THIRD_PARTY_SCRIPT_SUCCESS,
    FETCH_THIRD_PARTY_SCRIPT_FAILED
)<AdMaterialReq, ThirdPartyViewModel, ErrorHandler>();

export const submitThirdPartyTag = createAsyncAction(
    SUBMIT_THIRD_PARTY_TAG_REQUEST,
    SUBMIT_THIRD_PARTY_TAG_SUCCESS,
    SUBMIT_THIRD_PARTY_TAG_FAILED
)<
    ThirdPartyUpdateValues,
    ThirdPartyUpdateValues,
    ErrorHandler & { materialSpecificationId?: number; adMaterialId?: number }
>();

export const submitNewCreativeThirdPartyTag = createAsyncAction(
    SUBMIT_NEW_CREATIVE_THIRD_PARTY_TAG_REQUEST,
    SUBMIT_NEW_CREATIVE_THIRD_PARTY_TAG_SUCCESS,
    SUBMIT_NEW_CREATIVE_THIRD_PARTY_TAG_FAILED
)<NewMaterialThirdPartyValues, MatSpecsNewThirdPartyMaterial, ErrorHandler>();

export const fetchValidationReport = createAsyncAction(
    FETCH_VALIDATION_REPORT_REQUEST,
    FETCH_VALIDATION_REPORT_SUCCESS,
    FETCH_VALIDATION_REPORT_FAILED
)<number, { materialId: number; preflightReport: PreflightReport }, ErrorHandler>();

export const fetchPrintValidationReport = createAsyncAction(
    FETCH_PRINT_VALIDATION_REPORT_REQUEST,
    FETCH_PRINT_VALIDATION_REPORT_SUCCESS,
    FETCH_PRINT_VALIDATION_REPORT_FAILED
)<number, { materialId: number; preflightReport: PrintPreflightReport }, ErrorHandler>();

export const removeMaterial = createAsyncAction(
    REMOVE_MATERIAL_REQUEST,
    REMOVE_MATERIAL_SUCCESS,
    REMOVE_MATERIAL_FAILED
)<
    { materialSpecId: number; materialId: number; updateMaterialIds(materialSpecId: number, materialId: number): void },
    { materialSpecId: number; deletedMaterialId: number; materials: MaterialDetailsViewModel[] },
    ErrorHandler
>();

export const updateMaterialSpecStatus = createAsyncAction(
    UPDATE_MATERIAL_SPEC_STATUS_REQUEST,
    UPDATE_MATERIAL_SPEC_STATUS_SUCCESS,
    UPDATE_MATERIAL_SPEC_STATUS_FAILED
)<
    { action: MaterialSpecActionType; materialSpecId: number; status: number; isStatusUpdated?: boolean },
    { materialSpec: MaterialSpecificationViewModel },
    ErrorHandler
>();

export const setMaterialSpecWorkingStatus = createAction(SET_MATERIAL_WORKING_STATUS)<{
    materialSpec: MaterialSpecificationViewModel;
}>();

export const setNewMaterialVersion = createAction(SET_NEW_MATERIAL_VERSION)<{
    materialSpecId?: number;
    selectedMaterialId?: number;
    material: MaterialDetailsViewModel;
}>();

export const submitPrintMaterialDescription = createAsyncAction(
    SUBMIT_PRINT_MATERIAL_DESCRIPTION_REQUEST,
    SUBMIT_PRINT_MATERIAL_DESCRIPTION_SUCCESS,
    SUBMIT_PRINT_MATERIAL_DESCRIPTION_FAILED
)<PrintMaterialDescription, PrintMaterialDescription, ErrorHandler>();

export const submitPrintMaterialAdServerProperties = createAsyncAction(
    SUBMIT_PRINT_MATERIAL_AD_SERVER_PROPERTIES_REQUEST,
    SUBMIT_PRINT_MATERIAL_AD_SERVER_PROPERTIES_SUCCESS,
    SUBMIT_PRINT_MATERIAL_AD_SERVER_PROPERTIES_FAILED
)<PrintMaterialAdServerProperties, PrintMaterialAdServerProperties, ErrorHandler>();

export const updateUploadedMaterial = createAction(UPDATE_UPLOADED_MATERIAL)<{
    newMaterial: CreativesUploadViewModel;
    oldMaterialId: number;
}>();

export const updateMaterialStatus = createAction(UPDATE_UPLOADED_MATERIAL_STATUS)<{
    materialId: number;
    status: Status;
}>();

export const updateExistingSpecProperties = createAsyncAction(
    FETCH_MATERIAL_SPEC_REQUEST,
    FETCH_MATERIAL_SPEC_SUCCESS,
    FETCH_MATERIAL_SPEC_FAILED
)<
    { materialSpecId: number },
    { specId: number; status: Status; permissions: Permission[]; materials: MaterialDetailsViewModel[] },
    ErrorHandler
>();

export const setSelectedPage = createAction(SET_SELECTED_PAGE)<{
    materialId: number;
    direction: "PREVIOUS" | "NEXT";
}>();
